import { Injectable, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, interval } from 'rxjs';
import { Router } from '@angular/router';
import { PlatformLocation } from '@angular/common';
import { environment } from '../../../environments/environment';
import { User } from '../interfaces/user.interface';
import { OtpVerificationRequest, ForgotPasswordRequest, ResetPasswordRequest, IsSignUpRequest } from '../../models/auth.model';
import { ResponseModel } from '../../models/response.model';
import { CustomerDetails, OtpLoginResponse } from '../../models/customer.model';
import { ClientDetails } from '../../models/client-details.model';
import { ConfigService } from './config.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly configService = inject(ConfigService);
  private readonly platformLocation = inject(PlatformLocation);
  
  // Add envUrl property to store the baseUrl from config
  private envUrl: string = environment.baseUrl;
  
  private readonly currentUserSubject = new BehaviorSubject<User | null>(null);
  private readonly showLoginFormSubject = new BehaviorSubject<boolean>(false);
  
  // Signals for reactive state management
  readonly currentUser = signal<User | null>(null);
  readonly isLoggedIn = signal<boolean>(false);
  readonly clientDetails = signal<ClientDetails | null>(null);
  
  readonly currentUser$ = this.currentUserSubject.asObservable();
  readonly showLoginForm$ = this.showLoginFormSubject.asObservable();

  // Storage keys
  private readonly USER_STORAGE_KEY = 'currentUser';
  private readonly CLIENT_DETAILS_STORAGE_KEY = 'clientDetails';
  private readonly USER_SESSION_TTL = 4 * 60 * 60 * 1000; // 4 hours
  private readonly CLIENT_DETAILS_TTL = 24 * 60 * 60 * 1000; // 24 hours - longer TTL for client details

  // Session monitoring
  private sessionCheckInterval: any = null;
  private readonly SESSION_CHECK_INTERVAL = 5000; // Check every 5 seconds

  constructor() {
    // Initialize from localStorage if available
    this.initializeAuthState();
    // Start session monitoring
    this.startSessionMonitoring();
    
    // Load configuration from sys-config.json
    this.configService.loadSysConfig(this.platformLocation).subscribe(config => {
      this.envUrl = config.baseUrl;
      
    });
  }

  private initializeAuthState(): void {
    const storedUser = this.getStoredUser();
    const storedClientDetails = this.getStoredClientDetails();
    
    if (storedUser) {
      this.setCurrentUser(storedUser);
    } else {
      this.setCurrentUser(null);
    }
    
    // Always initialize client details regardless of user login status
    if (storedClientDetails) {
      this.setClientDetails(storedClientDetails);
    } else {
      this.setClientDetails(null);
    }
  }

  private getStoredUser(): User | null {
    try {
      const userStr = localStorage.getItem(this.USER_STORAGE_KEY);
      if (!userStr) {
        
        return null;
      }

      const userData = JSON.parse(userStr);
      
      // Check if user data has expired
      if (userData.expiry && new Date().getTime() > userData.expiry) {
        
        this.removeStoredUser();
        return null;
      }

      
      return userData.user;
    } catch (error) {
      console.error('AuthService: Error parsing stored user data:', error);
      this.removeStoredUser();
      return null;
    }
  }

  private getStoredClientDetails(): ClientDetails | null {
    try {
      const clientDetailsStr = localStorage.getItem(this.CLIENT_DETAILS_STORAGE_KEY);
      if (!clientDetailsStr) {
       
        return null;
      }

      const clientData = JSON.parse(clientDetailsStr);
      
      // Check if client data has expired
      if (clientData.expiry && new Date().getTime() > clientData.expiry) {
       
        this.removeStoredClientDetails();
        return null;
      }

      
      return clientData.clientDetails;
    } catch (error) {
      console.error('AuthService: Error parsing stored client details:', error);
      this.removeStoredClientDetails();
      return null;
    }
  }

  private storeUser(user: User): void {
    try {
      const userData = {
        user,
        expiry: new Date().getTime() + this.USER_SESSION_TTL
      };
      localStorage.setItem(this.USER_STORAGE_KEY, JSON.stringify(userData));
    } catch (error) {
      console.error('AuthService: Error storing user data:', error);
    }
  }

  private storeClientDetails(clientDetails: ClientDetails): void {
    try {
      const clientData = {
        clientDetails,
        expiry: new Date().getTime() + this.CLIENT_DETAILS_TTL
      };
      localStorage.setItem(this.CLIENT_DETAILS_STORAGE_KEY, JSON.stringify(clientData));
    } catch (error) {
      console.error('AuthService: Error storing client details:', error);
    }
  }

  private removeStoredUser(): void {
    try {
      localStorage.removeItem(this.USER_STORAGE_KEY);
    } catch (error) {
      console.error('AuthService: Error removing stored user data:', error);
    }
  }

  private removeStoredClientDetails(): void {
    try {
      localStorage.removeItem(this.CLIENT_DETAILS_STORAGE_KEY);
    } catch (error) {
      console.error('AuthService: Error removing stored client details:', error);
    }
  }

  private setCurrentUser(user: User | null): void {
    this.currentUser.set(user);
    this.isLoggedIn.set(!!user);
    this.currentUserSubject.next(user);
    
    if (user) {
      this.storeUser(user);
    } else {
      this.removeStoredUser();
    }
  }

  private setClientDetails(clientDetails: ClientDetails | null): void {
    this.clientDetails.set(clientDetails);
    
    if (clientDetails) {
      this.storeClientDetails(clientDetails);
    } else {
      this.removeStoredClientDetails();
    }
  }

  private clearAuthState(): void {
    // Only clear user data, keep client details
    this.setCurrentUser(null);
    // Note: We don't clear client details here
  }

  // Method to set client details
  setClientDetailsManually(clientDetails: ClientDetails): void {
    this.setClientDetails(clientDetails);
  }

  // Method to get client details
  getClientDetails(): ClientDetails | null {
    return this.clientDetails();
  }

  // Method to get client ID
  getClientId(): number | null {
    const clientDetails = this.clientDetails();
    return clientDetails?.clientId || null;
  }

  // Method to clear client details (only when explicitly needed)
  clearClientDetails(): void {
    this.setClientDetails(null);
  }

  showLoginForm(): void {
    this.showLoginFormSubject.next(true);
  }

  hideLoginForm(): void {
    this.showLoginFormSubject.next(false);
  }

  getCurrentLoginFormState(): boolean {
    return this.showLoginFormSubject.value;
  }

  login(credentials: { username: string; password: string }): Observable<any> {
    return this.http.post(`${this.envUrl}/auth/login`, credentials);
  }

  // Method to handle successful login
  handleSuccessfulLogin(user: User): void {
    this.setCurrentUser(user);
    this.startSessionMonitoring();
  }

  // Method to handle successful login with client details
  handleSuccessfulLoginWithClientDetails(user: User, clientDetails: ClientDetails): void {
    this.setCurrentUser(user);
    this.setClientDetails(clientDetails);
    this.startSessionMonitoring();
  }

  logout(): void {
    this.clearAuthState();
    this.hideLoginForm();
    this.stopSessionMonitoring();
    // Navigate to home page
    this.router.navigate(['/']);
  }

  verifyOtp(phone: string, otp: string): Observable<any> {
    return this.http.post(`${this.envUrl}/auth/verify-otp`, { phone, otp });
  }

  // Forgot Password API - Step 1: Send OTP
  forgotPassword(mobileNumber: string): Observable<ResponseModel> {
    const url = `${this.envUrl}/api/B2COnlineBooking/${mobileNumber}/ForgetPassword`;
    return this.http.get<ResponseModel>(url);
  }

  // Forgot Password API - Step 2: Reset Password
  resetPassword(mobileNumber: string, password: string): Observable<ResponseModel> {
    const url = `${this.envUrl}/api/B2COnlineBooking/CustomerResetPassword`;
    const payload: ResetPasswordRequest = {
      mobileNumber: mobileNumber,
      password: password,
      customerId: 0,
      returnSuccess: true,
      returnMessage: ""
    };
    return this.http.post<ResponseModel>(url, payload);
  }

    // OTP Login API - Step 1: Send OTP
  otpLogin(mobileNumber: string): Observable<ResponseModel> {
    const url = `${this.envUrl}/api/B2COnlineBooking/${mobileNumber}/ForgetPassword`;
    return this.http.get<ResponseModel>(url);
  }

  // OTP Verification API for Login
  verifyOtpForLogin(mobileNumber: string, otp: string, clientId: number): Observable<ResponseModel> {
    const url = `${this.envUrl}/api/B2COnlineBooking/B2CVerifyOtp`;
    const payload: OtpVerificationRequest = {
      name: "",
      clientId: clientId,
      mobileNumber: mobileNumber,
      emailAddress: "",
      otp: otp,
      returnSuccess: true,
      returnMessage: "",
      accessfrom: "OTP",
      transactionId: "",
      countryCode: ""
    };
    return this.http.post<ResponseModel>(url, payload);
  }


IsSignUp(mobileNumber: string, name: string = "", clientId: number = 0, emailAddress: string = "", password: string = "", address: string = "", gstNnumber: string = "",  otp: string = "", countryCode: string = ""): Observable<ResponseModel> {
  const url = `${this.envUrl}/api/B2COnlineBooking/CustomerSignup`;
  const payload: IsSignUpRequest = {
    MobileNumber: mobileNumber,
    Name: name,
    ClientId: clientId,
    //MobileNumber: null,
    EmailAddress: emailAddress,
    Password: password,
    Address: address,
    GSTNnumber: gstNnumber,
    OTP: otp,
    CountryCode: countryCode
  };
  return this.http.post<ResponseModel>(url, payload);
}





  // OTP Verification API for Forgot Password
  verifyOtpForForgotPassword(mobileNumber: string, otp: string, clientId: number): Observable<ResponseModel> {
    const url = `${this.envUrl}/api/B2COnlineBooking/B2CVerifyOtp`;
    const payload: OtpVerificationRequest = {
      name: "",
      clientId: clientId,
      mobileNumber: mobileNumber,
      emailAddress: "",
      otp: otp,
      returnSuccess: true,
      returnMessage: "",
      accessfrom: "OTP",
      transactionId: "",
      countryCode: ""
    };
    return this.http.post<ResponseModel>(url, payload);
  }

  // Get Customer Details API
  getCustomerDetails(customerId: number): Observable<ResponseModel> {
    const url = `${this.envUrl}/api/B2COnlineBooking/${customerId}/GetCustomerDetails`;
    return this.http.get<ResponseModel>(url);
  }

  // Get current user synchronously
  getCurrentUser(): User | null {
    return this.currentUser();
  }

  // Check if user is logged in synchronously
  isUserLoggedIn(): boolean {
    return this.isLoggedIn();
  }

  // Method to manually refresh user state from storage
  refreshUserState(): void {
    this.initializeAuthState();
  }

  // Debug method to check session status
  debugSessionStatus(): void {
    
    
    // Check remaining time for user session
    try {
      const userStr = localStorage.getItem(this.USER_STORAGE_KEY);
      if (userStr) {
        const userData = JSON.parse(userStr);
        const now = new Date().getTime();
        const remainingTime = userData.expiry - now;
        
      } else {
       
      }
    } catch (error) {
      
    }
    
    // Check remaining time for client details
    try {
      const clientStr = localStorage.getItem(this.CLIENT_DETAILS_STORAGE_KEY);
      if (clientStr) {
        const clientData = JSON.parse(clientStr);
        const now = new Date().getTime();
        const remainingTime = clientData.expiry - now;
        
      } else {
        
      }
    } catch (error) {
     
    }
    
   
  }

  private startSessionMonitoring(): void {
    // Clear any existing interval
    this.stopSessionMonitoring();
    
    this.sessionCheckInterval = interval(this.SESSION_CHECK_INTERVAL).subscribe(() => {
      this.checkSessionExpiry();
    });
  }

  private stopSessionMonitoring(): void {
    if (this.sessionCheckInterval) {
      this.sessionCheckInterval.unsubscribe();
      this.sessionCheckInterval = null;
    }
  }

  private checkSessionExpiry(): void {
    try {
      const userStr = localStorage.getItem(this.USER_STORAGE_KEY);
      if (userStr) {
        const userData = JSON.parse(userStr);
        const now = new Date().getTime();
        const expiry = userData.expiry;

        if (expiry && now > expiry) {
         
          this.logout();
        } else {
          const remainingTime = Math.max(0, (expiry - now) / 1000);
          
        }
      } else {
        
      }
    } catch (error) {
      console.error('AuthService: Error checking session expiry:', error);
    }
  }
} 